import { createI18n } from "vue-i18n";
import {
  locale as devExtremeLocale,
  loadMessages as loadDevExtremeMessages,
} from "devextreme/localization";
import nlMessages from "devextreme/localization/messages/nl.json";
import deMessages from "devextreme/localization/messages/de.json";
import authService from "../services/auth.service";
import filtersService from "../services/filters.service";

const supportedLocales = ["nl", "de"];
const messagesMap = {
  nl: nlMessages,
  de: deMessages,
};

let i18nInstance = null;

async function initializeI18n() {
  try {
    const defaultLocale = await authService.getLanguageCode(); // Fetch the locale from the server
    const messages = await loadMessages(defaultLocale);

    i18nInstance = createI18n({
      locale: defaultLocale, // Set the locale to the fetched locale
      fallbackLocale: defaultLocale == "nl" ? "de" : "nl",
      legacy: false, // you must set `false` to use Composition API,
      messages: {
        [defaultLocale]: messages.default,
        ["en"]: await import("./locales/en.json"), // Always load en messages since we need them on English schemes
      },
    });

    // Initialize localization for DevExtreme
    setupDevExtremeLocalization(defaultLocale);

    return i18nInstance;
  } catch (error) {
    console.error("Error initializing i18n", error);
    return createFallbackI18n();
  }
}

async function loadMessages(locale) {
  const messages = await import(`./locales/${locale}.json`);
  return messages;
}

// Load DevExtreme messages and set the locale
function setupDevExtremeLocalization(locale) {
  if (messagesMap[locale]) {
    loadDevExtremeMessages(messagesMap[locale]);
  }
  devExtremeLocale(locale);
}

function createFallbackI18n() {
  // Return a fallback i18n instance with default settings if dynamic loading fails
  return createI18n({
    locale: "nl",
    fallbackLocale: "nl",
    legacy: false,
    messages: {},
  });
}

// Function to switch language
// The language switcher component is hidden currently
async function switchLanguage(newLocale) {
  try {
    const messages = await loadMessages(newLocale); // Loading of translation files before the locale is switched
    i18nInstance.global.setLocaleMessage(newLocale, messages); // Set messages for the new locale
    i18nInstance.global.locale.value = newLocale; // Update the locale
    localStorage.setItem("user-locale", newLocale); // Save the chosen locale into local storage, will be useful if we implement 'guessing' the preferred locale

    // Update DevExtreme localization
    setupDevExtremeLocalization(newLocale);

    await filtersService.getDefaultFilters(newLocale);
  } catch (error) {
    console.error("Error switching language", error);
  }
}

export { initializeI18n, supportedLocales, switchLanguage, loadMessages, i18nInstance };
